// src/services/deals.ts
import { apiGet } from "./baseFetch";

export type DealRaw = {
  kc_url?: string;
  [key: string]: any;
};

export type DealRow = DealRaw & {
  id: string;
};

export type GetDealsOptions = {
  delay?: number;
  signal?: AbortSignal;
};

export type SearchDealsParams = {
  search?: string;
  platform?: string;
  industry?: string;
  status?: string;
  momentum?: string;
  sortKey?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
  delay?: number;      // accepted to match callers, even if unused
  signal?: AbortSignal;
};

export type SearchDealsResponse<TItem = DealRow> = {
  items: TItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
};

export async function getDeals(
  options: GetDealsOptions = {}
): Promise<DealRow[]> {
  const { delay = 800, signal } = options;

  const data = await apiGet<DealRaw[]>("/deals", { signal });

  const rows: DealRow[] = data.map((row, index) => ({
    id: row.kc_url || String(index),
    ...row,
  }));

  if (delay) {
    await new Promise((r) => setTimeout(r, delay));
  }

  return rows;
}

export async function searchDeals(
  params: SearchDealsParams = {}
): Promise<SearchDealsResponse<DealRow>> {
  const {
    search = "",
    platform = "",
    industry = "",
    status = "",
    momentum = "",
    sortKey = "amount_raised",
    sortDir = "desc",
    page = 1,
    pageSize = 10,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    delay, // kept for signature compatibility
    signal,
  } = params;

  const qs = new URLSearchParams();

  if (search) qs.set("search", search);
  if (platform) qs.set("platform", platform);
  if (industry) qs.set("industry", industry);
  if (status) qs.set("status", status);
  if (momentum) qs.set("momentum", momentum);

  qs.set("sort_key", sortKey);
  qs.set("sort_dir", sortDir);
  qs.set("page", String(page));
  qs.set("page_size", String(pageSize));

  const data = await apiGet<SearchDealsResponse<DealRaw>>(
    `/deals/search?${qs.toString()}`,
    { signal }
  );
  // data = { items, total, page, page_size, total_pages }

  const itemsWithId: DealRow[] = data.items.map((row, index) => ({
    id: row.kc_url || String(index),
    ...row,
  }));

  return { ...data, items: itemsWithId };
}
